import { useState } from 'react';
import {
  Page, Card, IndexTable, Text, Badge, Banner, Button, Box, InlineStack,
  BlockStack, ChoiceList, Toast, Select,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  listTireJobs,
  createTireJob,
  type TireImportJob,
  type TireVendor,
} from '../api/tires';

const TIRE_BRANDS = [
  'Nitto', 'Falken', 'BFGoodrich', 'Goodyear', 'Michelin', 'Continental',
  'Bridgestone', 'Firestone', 'Hankook', 'Kumho', 'Yokohama', 'Cooper',
  'General', 'Mickey Thompson', 'Pirelli', 'Pro Comp', 'Atturo', 'Westlake',
  'Radar', 'Nexen', 'Ironman',
];

export function TireJobsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [vendor, setVendor] = useState<TireVendor>('ATD');
  const [selectedBrands, setSelectedBrands] = useState<string[]>(['Nitto']);
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tire-jobs'],
    queryFn: listTireJobs,
    // While any job is pending/processing, poll every 3s. The processor
    // runs inline server-side; status flips pending → processing → preview
    // (or → failed) over ~30-90 seconds for a full Nitto pull.
    refetchInterval: (query) => {
      const rows = (query.state.data as any)?.data as TireImportJob[] | undefined;
      if (rows?.some((j) => j.status === 'pending' || j.status === 'processing')) return 3000;
      return false;
    },
  });

  const create = useMutation({
    mutationFn: createTireJob,
    onSuccess: (resp) => {
      setToast(`Tire pull job ${resp.id} started`);
      qc.invalidateQueries({ queryKey: ['tire-jobs'] });
      navigate(`/tires/jobs/${resp.id}`);
    },
    onError: (e: Error) => setToast(`Failed: ${e.message}`),
  });

  const jobs = data?.data ?? [];
  const inFlight = jobs.find((j) => j.status === 'pending' || j.status === 'processing');

  const distinctVendors = Array.from(new Set(jobs.map((j) => j.vendor))).sort();
  const filteredJobs =
    vendorFilter === 'all' ? jobs : jobs.filter((j) => j.vendor === vendorFilter);
  const vendorFilterOptions = [
    { label: `All vendors (${jobs.length})`, value: 'all' },
    ...distinctVendors.map((v) => ({
      label: `${v} (${jobs.filter((j) => j.vendor === v).length})`,
      value: v,
    })),
  ];

  const headings: [{ title: string }, ...{ title: string }[]] = [
    { title: 'ID' },
    { title: 'Vendor' },
    { title: 'Source' },
    { title: 'Status' },
    { title: 'Raw' },
    { title: 'Valid' },
    { title: 'Skipped' },
    { title: 'Toyo' },
    { title: 'Review' },
    { title: 'Created' },
  ];

  return (
    <>
      <Page
        title="Tire imports"
        subtitle="Pull tires from ATD (or upload JSON manually) → preview → apply"
      >
        {error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical">Failed to load tire jobs: {(error as Error).message}</Banner>
          </Box>
        )}

        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">Run ATD tire pull</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Calls the ATD SOAP API for the selected brands across all 6 tire
                ProductGroups and stages results in tire_import_rows. Toyo is
                rejected at the request level. Nothing is pushed to Shopify
                from here — apply step writes to tire_products only.
              </Text>

              <ChoiceList
                title="Vendor"
                choices={[
                  { label: 'ATD (live SOAP API)', value: 'ATD' },
                  { label: 'WheelPros (when feed available)', value: 'WheelPros', disabled: true },
                  { label: 'Manual JSON upload', value: 'Manual', disabled: true },
                ]}
                selected={[vendor]}
                onChange={(s) => setVendor((s[0] as TireVendor) ?? 'ATD')}
              />

              <ChoiceList
                allowMultiple
                title="Brands (Toyo intentionally absent)"
                choices={TIRE_BRANDS.map((b) => ({ label: b, value: b }))}
                selected={selectedBrands}
                onChange={(s) => setSelectedBrands(s as string[])}
              />

              <InlineStack gap="200">
                <Button
                  variant="primary"
                  loading={create.isPending}
                  disabled={!!inFlight || selectedBrands.length === 0}
                  onClick={() => {
                    console.info('[tire-imports] run pull clicked', {
                      vendor,
                      brands: selectedBrands,
                    });
                    create.mutate({
                      vendor,
                      source_kind: 'atd_soap',
                      brands: selectedBrands,
                    });
                  }}
                >
                  {inFlight ? `Job ${inFlight.id} ${inFlight.status}…` : 'Run pull'}
                </Button>
                <Text as="span" variant="bodySm" tone="subdued">
                  {selectedBrands.length} brand{selectedBrands.length === 1 ? '' : 's'} selected
                </Text>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card padding="0">
            <Box padding="400">
              <InlineStack align="space-between" blockAlign="center" wrap gap="300">
                <Text as="h3" variant="headingMd">Recent jobs</Text>
                <Select
                  label="Filter by vendor"
                  labelHidden
                  options={vendorFilterOptions}
                  value={vendorFilter}
                  onChange={setVendorFilter}
                />
              </InlineStack>
            </Box>
            <IndexTable
              resourceName={{ singular: 'job', plural: 'jobs' }}
              itemCount={filteredJobs.length}
              loading={isLoading}
              selectable={false}
              headings={headings}
            >
              {filteredJobs.map((job, i) => (
                <IndexTable.Row
                  id={String(job.id)}
                  key={job.id}
                  position={i}
                  onClick={() => navigate(`/tires/jobs/${job.id}`)}
                >
                  <IndexTable.Cell>{job.id}</IndexTable.Cell>
                  <IndexTable.Cell><Badge>{job.vendor}</Badge></IndexTable.Cell>
                  <IndexTable.Cell>{job.source_kind}</IndexTable.Cell>
                  <IndexTable.Cell><JobStatusBadge status={job.status} /></IndexTable.Cell>
                  <IndexTable.Cell>{job.raw_row_count}</IndexTable.Cell>
                  <IndexTable.Cell>{job.valid_count}</IndexTable.Cell>
                  <IndexTable.Cell>{job.skipped_count}</IndexTable.Cell>
                  <IndexTable.Cell>{job.toyo_skipped_count}</IndexTable.Cell>
                  <IndexTable.Cell>{job.needs_review_count}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {new Date(job.created_at).toLocaleString()}
                    </Text>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>
        </BlockStack>
      </Page>
      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

function JobStatusBadge({ status }: { status: TireImportJob['status'] }) {
  const tone =
    status === 'applied' ? 'success' :
    status === 'preview' ? 'info' :
    status === 'failed' ? 'critical' :
    status === 'processing' ? 'attention' :
    'new';
  return <Badge tone={tone}>{status}</Badge>;
}
